import axios from 'axios';
import { config } from '../../../config/env';
import {
  CustomerMinimal,
  RawCustomerMinimal,
  ListRegisteredCustomersSummaryParams,
  PaginatedCustomersMinimal,
  PaginatedRegisteredCustomersSummary,
} from './types';

const url = `${config.customerManagement.apiUrl}/customers/summary`;

export async function listRegisteredCustomersSummary(
  params: ListRegisteredCustomersSummaryParams,
): Promise<PaginatedRegisteredCustomersSummary> {
  const { data } = await axios.get<PaginatedRegisteredCustomersSummary>(url, {
    params,
  });
  return data;
}

const minimalUrl = `${config.customerManagement.apiUrl}/customers/minimal`;

/** O serviço rejeita `limit` acima de 500. */
const MINIMAL_PAGE_LIMIT = 500;

/**
 * Teto de segurança contra um `meta.total_pages` inconsistente virar laço
 * infinito no navegador. 50 páginas de 500 cobrem 25 mil clientes — o maior
 * escritório medido tem ~13 mil.
 *
 * Bater nele NÃO é um limite de negócio: significa que a resposta do serviço não
 * é confiável, ou que existe escritório além de tudo o que foi medido. Nos dois
 * casos a lista sai incompleta, então o fluxo falha alto em vez de devolver
 * parcial (ver o `throw` abaixo).
 */
const MINIMAL_MAX_PAGES = 50;

/**
 * Todos os clientes do escritório, paginando até o fim.
 *
 * A paginação de `/customers/minimal` vive em `meta`, não na raiz da resposta.
 * O escritório vem do `lawyer_id` derivado do JWT — não se manda nada aqui.
 */
export async function fetchAllCustomersMinimal(
  archived: boolean,
): Promise<CustomerMinimal[]> {
  const customers: CustomerMinimal[] = [];
  let page = 1;
  let totalPages = 1;

  do {
    const { data } = await axios.get<PaginatedCustomersMinimal>(minimalUrl, {
      params: { page, limit: MINIMAL_PAGE_LIMIT, archived },
    });

    for (const customer of data?.data ?? []) {
      customers.push({ ...customer, id: Number(customer.id) });
    }

    totalPages = data?.meta?.total_pages ?? 1;
    page += 1;
  } while (page <= totalPages && page <= MINIMAL_MAX_PAGES);

  /*
    Só sobra página quando foi o teto que parou o laço — no caminho normal o
    laço sai com `page > totalPages`. Devolver aqui publicaria uma lista
    incompleta como se fosse completa, e o combo esconderia clientes sem sinal
    nenhum. Falhar deixa o `loadCustomers` cair em LOAD_CUSTOMERS_FAILED.
  */
  if (page <= totalPages) {
    throw new Error(
      `Listagem de clientes truncada: o serviço reportou ${totalPages} páginas, ` +
        `acima do teto de ${MINIMAL_MAX_PAGES}.`,
    );
  }

  return customers;
}

const createUrl = `${config.customerManagement.apiUrl}/customers`;

/**
 * Cria cliente só com o nome — que é tudo o que os modais de cadastro coletam
 * (o formulário tem um campo).
 *
 * Manda **apenas** `name` de propósito: o serviço roda com
 * `forbidNonWhitelisted`, e os campos que o payload legado carregava vazios
 * (`rg`, `profession`, `address`, `phone_1`…) não existem no `CreateCustomerDto`
 * — mandá-los devolve 400.
 *
 * O escritório vem do `lawyer_id` derivado do JWT.
 */
export async function createCustomerByName(
  name: string,
): Promise<CustomerMinimal> {
  const { data } = await axios.post<RawCustomerMinimal>(createUrl, { name });
  return { ...data, id: Number(data.id) };
}
